import React, { useEffect, useState } from 'react'

import Link from 'next/link'

import {
  Dialog,
  DialogBackdrop,
  Button as DialogButton,
  DialogPanel,
  DialogTitle,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react'
import clsx from 'clsx'

import { Button } from '@ui/components/buttons/Button'
import { marketingUrl } from '@ui/lib/urls'
import { OfferProps, PartnerData } from '@ui/types'

const partnerATMCashAdvance: PartnerData = {
  name: 'ATM.com',
  title: 'ATM Benefits',
  logo: '/img/partners/atm.png',
  height: '32px',
  width: '109px',
  type: 'Cash Advance',
  features: [
    'Fast cash advances up to $100 without credit checks',
    'No late fees, no credit checks, no interest',
    'No payroll or direct deposit changes required',
    'Budgeting and spend tracking tools included',
    'Earn money through surveys, games, and offers',
  ],
  callouts: ['No interest, late fees, credit check', 'Budgeting tools'],
  amount: '$25-$100',
  monthly_payment: '$4.99/mo',
  fast_funding_fee: '$5',
  apy: '0%',
  description: (
    <div className="space-y-2">
      <p>
        ATM.com provides a fast and straightforward way to access up to $100 in
        cash advances without worrying about credit checks or interest. Perfect
        for covering small, unexpected expenses, ATM.com offers budgeting tools
        and the opportunity to earn extra cash through surveys, games, and
        offers.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
  disclaimer: (
    <div className="space-y-2">
      <p>
        Eligibility is based on review and approval. Not all applicants will be
        eligible for ATM Instant Cash™. Transfer fees may apply. Monthly $4.99
        subscription req.
      </p>
      <p>
        For more information, please refer to{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href={marketingUrl('/terms-of-service')}
        >
          ATM.com Terms of Service
        </Link>
        .
      </p>
    </div>
  ),
}

const partnerKloverCashAdvance: PartnerData = {
  name: 'Klover',
  title: 'Klover Benefits',
  logo: '/img/partners/klover.png',
  height: '32px',
  width: '128px',
  type: 'Cash Advance',
  features: [
    'Cash advances up to $200 with no interest or late fees',
    'Earn points for surveys and watching ads',
    'Budgeting tools and personalized financial insights',
  ],
  callouts: ['No interest or late fees', 'Financial insights'],
  amount: 'Up to $200',
  monthly_payment: '$3.99/mo',
  fast_funding_fee: '$1.49-$20.78',
  apy: '0%',
  description: (
    <div className="space-y-2">
      <p>
        Klover offers cash advances up to $200 with no interest, late fees, or
        hidden costs. Designed for users who want more than just financial
        support, Klover includes tools to help you earn rewards, manage your
        budget, and gain personalized financial insights.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
  disclaimer: (
    <div className="space-y-2">
      <p>
        Klover does not offer loans. We are NOT a payday loan, cash loan, or
        personal loan app, nor an app to borrow money. By validating your
        employment you can access money you’ve already earned whenever you want
        to. Klover’s advance service is free and doesn’t have interest. Our APR
        is 0%. Klover’s advance service does not require a minimum repayment
        time frame. Klover’s advance service does not require a maximum
        repayment time frame. For example, if you request an advance of $100
        your future repayment is $100. Example 2: If you select a completely
        optional immediate delivery fee of $8.49 your future repayment would be
        $108.49. Klover’s advance service is not a contracted obligation to
        repay a loan.
      </p>
      <p>
        For more information, please refer to{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href="https://www.klover.app/terms-and-conditions"
        >
          Klover Terms and Conditions
        </Link>
        .
      </p>
    </div>
  ),
}

const partnerBrigitCashAdvance: PartnerData = {
  name: 'Brigit',
  title: 'Brigit Benefits',
  logo: '/img/partners/brigit.png',
  height: '32px',
  width: '78px',
  type: 'Cash Advance',
  features: [
    'Access up to $250 with no interest or hidden fees',
    'No late fees or credit checks',
    'Automatic cash advances to prevent overdraft fees',
    'Express Delivery for fast cash access',
    'Budgeting tools and credit monitoring',
  ],
  callouts: ['No late fees or credit checks', 'Budgeting tools'],
  amount: '$50-$250',
  monthly_payment: '$8.99-$14.99/mo',
  fast_funding_fee: '$0.99-$3.99',
  apy: '0%',
  description: (
    <div className="space-y-2">
      <p>
        Brigit provides up to $250 in interest-free cash advances to help
        prevent overdraft fees and cover urgent financial needs. With features
        like automatic advances, credit monitoring, and express delivery
        options, Brigit supports your financial wellness and helps you stay on
        top of your budget.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
  disclaimer: (
    <div className="space-y-2">
      <p>
        ¹ Not all users will qualify. Advances range $50-$250, avg advance $73
        from 02/2018 to 07/2024. Express transfer fee may apply. Monthly $8.99
        subscription req. Cancel anytime. Instant Cash is not available in all
        states.
      </p>
      <p>
        ² Not all users will qualify for Credit Builder loans. Impact to score
        may vary. Some users’ scores may not improve. Monthly $14.99
        subscription req. Cancel anytime. Credit Builder loans are not available
        in all states.
      </p>
      <p>
        The Brigit Credit Builder is a service provided by Brigit and its bank
        partner, Coastal Community Bank, Member FDIC. The Brigit Credit Builder
        product is separate from the Brigit Instant Cash Advance service. Brigit
        Credit Builder installment loans are issued by Coastal Community Bank,
        Member FDIC, subject to approved underwriting practices.
      </p>
      <p>
        For more information, please refer to{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href="https://www.hellobrigit.com/terms"
        >
          Brigit Terms of Service
        </Link>
        .
      </p>
    </div>
  ),
}

const partnerCurrentCashAdvance: PartnerData = {
  name: 'Current',
  title: 'Current Benefits',
  logo: '/img/partners/current.png',
  height: '32px',
  width: '125px',
  type: 'Earned Wage Access',
  features: [
    'Access paycheck advances up to $500',
    'Get paid up to two days faster with direct deposit',
    'Fee-free overdrafts up to $100',
    'Spending insights and cashback rewards',
    'Build credit with everyday purchases',
  ],
  callouts: ['Overdraft prevention', 'Spending insights'],
  amount: '$50-$500',
  monthly_payment: 'Free',
  fast_funding_fee: 'N/A',
  apy: '0%',
  description: (
    <div className="space-y-2">
      <p>
        Current combines earned wage access with innovative banking features,
        letting you access paycheck advances up to $500. With no monthly fees
        and benefits like spending insights, cashback rewards, and fee-free
        overdrafts, Current helps you manage your money smarter and faster.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
  disclaimer: (
    <div className="space-y-2">
      <p>
        ¹ Paycheck Advance is for eligible customers only. Your actual available
        Paycheck Advance amount will be displayed to you in the mobile app and
        may change from time to time. Conditions and eligibility may vary and
        are subject to change at any time, at the sole discretion of Finco
        Advance LLC, which offers this optional feature. Finco Advance LLC is a
        financial technology company, not a bank. Refer to{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href="https://current.com/docs/paycheck_advance_terms/"
        >
          Paycheck Advance Terms and Conditions
        </Link>
        .
      </p>
      <p>
        ² Expedited disbursement of your Paycheck Advance is an optional feature
        that is subject to an Instant Access Fee and may not be available to all
        users. Expedited disbursements may take up to an hour. For more
        information, please refer to{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href="https://current.com/docs/paycheck_advance_terms/"
        >
          Paycheck Advance Terms and Conditions
        </Link>
        .
      </p>
    </div>
  ),
}

const partnerWiselyCashAdvance: PartnerData = {
  name: 'Wisely by ADP',
  title: 'Wisely Benefits',
  logo: '/img/partners/wisely.png',
  height: '32px',
  width: '125px',
  type: 'Earned Wage Access',
  features: [],
  callouts: [
    'Get paid up to 2 days early with direct deposit',
    'No cost to you',
  ],
  amount: 'Access to your pay',
  monthly_payment: '$0',
  fast_funding_fee: 'None',
  apy: '0%',
  description: (
    <div className="space-y-2">
      <p>
        Get paid up to 2 days early¹, earn cash back rewards², and spend, plan,
        and save on your terms with Wisely®.
      </p>
      <p>
        Brought to you by ADP®, a Wisely® card and myWisely mobile app³ put
        you in charge of your money and give you more flexibility and control at
        every step, so you can accomplish your everyday goals and also see way
        beyond payday.
      </p>
      <p>
        With early direct deposit, you can get paid up to 2 days early². Get
        early pay from multiple jobs, government benefits, and tax refunds, too.
        Best yet? Take your Wisely prepaid debit card with you, even if you
        change jobs⁶.
      </p>
      <p>
        With Wisely there are no minimum balance fees¹ or fees on everyday
        spending. No annual fees, overdraft fees, or fees for in-network ATMs⁷
        either. When there are certain types of fees associated with your card,
        you can count on us to be transparent about it.
      </p>
    </div>
  ),
  disclaimer: (
    <div className="space-y-2">
      <p>1. Standard message fees and data rates may apply.</p>
      <p>
        2. You must opt-in to early direct deposit on mywisely.com or myWisely
        mobile app. Early direct deposit of funds is not guaranteed and is
        subject to payor’s support and the timing of payor’s payment
        instruction. Faster funding claim is based on a comparison of our policy
        of making funds available upon our receipt of payment instruction with
        the typical banking practice of posting funds at settlement. Please see
        full disclosures on mywisely.com or myWisely app. Please allow up to 3
        weeks for funds to be loaded to the card after initial setup of direct
        deposit to your card. Additional Identification verification is
        required.
      </p>
      <p>
        3. Cash back rewards on purchases at participating merchants are powered
        by Dosh Rewards. Opt-In is required for Dosh Rewards only. Most Cash
        Back Rewards will appear in your Wisely Rewards savings envelope within
        the next calendar month following your completed transaction. Only Cash
        Back Rewards for the purchase of eGift cards will appear instantly.
        eGift Card Cash Back offers range from 2% – 12%, depending on the gift
        card that is purchased. Cash Back amounts will be disclosed before you
        select a gift card. Please review the Terms and Conditions of each eGift
        card product before purchase. Funds from all Rewards can be moved from
        the savings envelope into the available balance on your card. You must
        log in to myWisely to access the Rewards features for purchases and
        eGift cards. These optional offers are not Fifth Third Bank, Mastercard,
        or Visa products or services, nor does Fifth Third Bank, Mastercard, or
        Visa endorse these offers.
      </p>
      <p>
        4. To view applicable fees, please log in to the myWisely app or
        mywisely.com to see your cardholder agreement and list of all fees for
        more information.
      </p>
      <p>
        5. Please allow up to 3 weeks for your pay to be loaded to the card
        after initial setup of direct deposit to your card.
      </p>
      <p>
        6. The bill pay feature, powered by Papaya, is available through the
        myWisely app. Additional terms and limits apply. This optional offer is
        not a Fifth Third Bank, Pathward, Mastercard or Visa product or service,
        nor does Fifth Third Bank, Pathward, Mastercard or Visa endorse this
        offer.
      </p>
      <p>
        7. If you have a Wisely Pay card or Wisely Cash card (see back of your
        card), access to certain features or loading funds from sources other
        than the company you received your card through requires an upgrade,
        which you can request in the myWisely app or by calling the number on
        the back of your card. Requesting an upgrade will automatically initiate
        an identity verification process utilizing your personal information
        already on file which must be validated in order to upgrade. Card may be
        restricted or closed if your identity cannot be verified. Upgrade may
        not be available to all cardholders. If you have a Wisely Direct card
        (see back of card), you’re already upgraded.
      </p>
      <p>
        8. The number of fee-free ATM transactions may be limited. Please log in
        to the myWisely app or mywisely.com and see your cardholder agreement
        and list of all fees for more information.
      </p>
      <p>
        9. Your funds are protected from fraud if your card is lost or stolen.
        You must notify us immediately and assist us in our investigation if
        your card is lost or stolen or you believe someone is using your card
        without your permission. For more information, please review your
        cardholder agreement by logging in to the myWisely app or online at
        mywisely.com.
      </p>
      <p>
        10. See your Cardholder Agreement for full zero-liability information.
        Under MasterCard’s Zero Liability Policy, your liability for
        unauthorized transactions on your Card Account is $0.00 if you notify us
        promptly upon becoming aware of the loss or theft, and you exercise
        reasonable care in safeguarding your Card from loss, theft, or
        unauthorized use. These provisions limiting your liability do not apply
        to debit transactions not processed by MasterCard or to unregistered
        cards. Visa’s Zero Liability policy does not apply to certain commercial
        card and anonymous prepaid card transactions or transactions not
        processed by Visa. Cardholders must use care in protecting their card
        and notify their issuing financial institution immediately of any
        unauthorized use. Contact your issuer for more detail.”
      </p>
      <p>
        11. Amounts transferred to your savings envelope will no longer appear
        in your available balance. You can transfer money from your savings
        envelope back to your available balance at any time using the myWisely
        app mobile app or at mywisely.com.
      </p>
      <p>
        12. Peer Transfer transactions between enrolled users through Peer
        Transfer are final and cannot be reversed. A peer transfer recipient
        must be a primary card member who is actively enrolled in Wisely Peer
        Transfer before being sent a peer transfer. They cannot receive a peer
        transfer if they have not opted in. Peer transactions are typically
        processed within seconds but could take longer due to network timing,
        receiving bank processes, or other factors. Terms and limits apply.
        Please log in to the myWisely app or mywisely.com to see your cardholder
        agreement and transaction limits.
      </p>
      <p>
        13. While this feature is available without a fee, certain other
        transaction fees and costs, terms, and conditions are associated with
        the use of this card. Please log in to the myWisely app or mywisely.com
        to see your cardholder agreement and list of all fees for more
        information.
      </p>
      <p>
        14. The Wisely card is a prepaid card. The Wisely card is not a credit
        card and does not build credit.
      </p>
      <p>
        The Wisely Direct Mastercard is issued by Fifth Third Bank, N.A., Member
        FDIC. ADP is a registered ISO of Fifth Third Bank, N.A., or Pathward,
        N.A. The Wisely Direct Mastercard can be used where debit Mastercard is
        accepted. Mastercard and the circles design are registered trademarks of
        Mastercard International Incorporated. PayPal and Venmo are registered
        trademarks of PayPal, Inc.
      </p>
      <p>
        For more information, please refer to the myWisely app or{' '}
        <Link
          className="text-blueberry-700"
          target="_blank"
          href="https://www.mywisely.com/"
        >
          mywisely.com
        </Link>
        .
      </p>
    </div>
  ),
}

const partnerATMBudgeting: PartnerData = {
  name: 'ATM.com',
  title: 'ATM Benefits',
  logo: '/img/partners/atm.webp',
  height: '32px',
  width: '109px',
  monthly_payment: '$3.99 or $7.99/mo',
  type: 'Personal Finance and Budgeting',
  features: [
    'Comprehensive spending insights and tracking',
    'Personalized budgeting tools to achieve financial goals',
    'Subscription and bill tracking with alerts',
    'Low balance alerts and cashflow insights',
    'Earn money through surveys, games, and offers',
  ],
  callouts: [
    'Personalized budgeting and insights',
    'Cash-earning opportunities',
  ],
  best_for: 'Comprehensive spending insights and savings tools',
  description: (
    <div className="space-y-2">
      <p>
        ATM.com is a budgeting app designed to provide comprehensive insights
        into your spending while offering tools to save more effectively. With
        features like cashflow tracking, low-balance alerts, and subscription
        management, ATM.com empowers you to take control of your finances.
        Whether you're budgeting for monthly goals or looking for ways to earn
        extra money through surveys and games, ATM.com combines powerful
        financial tools with opportunities to boost your income.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
}

const partnerKloverBudgeting: PartnerData = {
  name: 'Klover',
  title: 'Klover Benefits',
  logo: '/img/partners/klover.png',
  height: '32px',
  width: '128px',
  monthly_payment: 'Up to $4.99/mo',
  type: 'Personal Finance and Budgeting',
  features: [
    'Gain insights into spending habits with detailed tracking',
    'Access to financial advising and credit monitoring',
    'Real-time price comparisons for smarter shopping',
    'Redeem points for Amazon gift cards',
    'Optional membership with enhanced financial tools',
  ],
  callouts: ['Rewards with Amazon gift cards', 'Real-time price comparisons'],
  best_for: 'Financial insights and membership benefits',
  description: (
    <div className="space-y-2">
      <p>
        Klover helps you level up your financial game with easy-to-use budgeting
        tools and financial insights. Gain a deeper understanding of your
        spending habits and track expenses with precision. With optional Klover+
        membership, access enhanced features like financial advising, credit
        monitoring, and the ability to redeem points for Amazon gift cards.
        Klover makes managing your money simple, all while offering cash
        advances without interest or hidden fees.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
}

const partnerMonarchMoneyBudgeting: PartnerData = {
  name: 'Monarch Money',
  title: 'Monarch Money Benefits',
  logo: '/img/partners/monarchmoney.png',
  height: '32px',
  width: '128px',
  monthly_payment: '$14.99/mo',
  annual_payment: '$69.99/yr',
  type: 'Comprehensive Financial Management',
  features: [
    'Link all financial accounts for a complete financial overview',
    'Set custom budgets and track net worth',
    'Create and manage personalized financial goals',
    'Detailed analytics to understand spending patterns',
    'High level of customization for tailored financial planning',
  ],
  callouts: [
    'Collaborative tools for couples/advisors',
    'Advanced customization options',
  ],
  best_for: 'Personalized financial planning and analytics',
  description: (
    <div className="space-y-2">
      <p>
        Monarch Money is a comprehensive financial management platform that
        helps you track, plan, and optimize your finances. Sync all your
        accounts to get a complete picture of your financial health, set
        personalized budgets, and track your net worth. Monarch’s collaborative
        tools make it ideal for couples or families to work together toward
        financial goals. With its intuitive design and detailed analytics,
        Monarch Money provides the clarity you need to make smarter financial
        decisions.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
}

const partnerHiatusBudgeting: PartnerData = {
  name: 'Hiatus',
  title: 'Hiatus Benefits',
  logo: '/img/partners/hiatus.png',
  height: '32px',
  width: '109px',
  monthly_payment: '$9.99/mo',
  annual_payment: '$36.00/yr',
  type: 'Bill & Subscription Management',
  features: [
    'Track and manage all your subscriptions in one place',
    'Receive alerts for upcoming bills to avoid late fees',
    'Negotiate bills with service providers to lower expenses',
    'Personalized budgeting tools for better financial control',
    'Financial insights to help optimize spending habits',
  ],
  callouts: [
    'Bill negotiation service',
    'Personalized spending improvement suggestions',
  ],
  best_for: 'Bill management and negotiation',
  description: (
    <div className="space-y-2">
      <p>
        Hiatus simplifies financial management by focusing on bill reduction and
        subscription tracking. It identifies opportunities to save on bills and
        negotiates on your behalf, helping you lower monthly expenses without
        the hassle. With features like spending insights and net worth tracking,
        Hiatus keeps you informed about your financial health. Whether you’re
        cutting down on unnecessary subscriptions or optimizing your spending,
        Hiatus is your go-to app for saving money and staying on track.
      </p>
      <p>
        <strong>Key Features</strong>
      </p>
    </div>
  ),
}

const partnerData: Record<string, PartnerData> = {
  'surveys-atm': {
    name: 'ATM.com',
    title: 'ATM Benefits',
    logo: '/img/partners/atm.webp',
    features: [
      'Earn real cash, not points, for surveys and more',
      'Multiple earning options, including games, surveys and offers',
      'Direct cash withdrawals to your bank account',
    ],
  },
  'surveys-inboxdollars': {
    name: 'InboxDollars',
    title: 'InboxDollars Benefits',
    logo: '/img/partners/inboxdollars.png',
    features: [
      'Multiple earning options',
      'Real cash payouts, not points',
      '$5 sign-up bonus for new users',
    ],
  },
  'surveys-mypoints': {
    name: 'MyPoints',
    title: 'MyPoints Benefits',
    logo: '/img/partners/mypoints.webp',
    features: [
      'Earn points for surveys, shopping, and more with no payout cap',
      'Access exclusive deals from 2,000+ retailers',
      'Bonus rewards even when disqualified from surveys',
    ],
  },
  'surveys-swagbucks': {
    name: 'Swagbucks',
    title: 'Swagbucks Benefits',
    logo: '/img/partners/swagbucks.webp',
    features: [
      'Earn rewards for surveys, shopping, and more',
      'Redeem points for gift cards or PayPal cash',
      'Mobile app for easy earning on the go',
    ],
  },
  'surveys-ysense': {
    name: 'ySense',
    title: 'ySense Benefits',
    logo: '/img/partners/ysense.webp',
    features: [
      'Earn cash for surveys and offers',
      'Easy mobile app for on-the-go tasks',
      'Referral bonuses up to 30%',
    ],
  },
  'cash-advance-apps-atm': {
    ...partnerATMCashAdvance,
  },
  'cash-advance-apps-brigit': {
    ...partnerBrigitCashAdvance,
  },
  'cash-advance-apps-current': {
    ...partnerCurrentCashAdvance,
  },
  'cash-advance-apps-klover': {
    ...partnerKloverCashAdvance,
  },
  'cash-advance-apps-sorbet': {
    name: 'Sorbet',
    title: 'Sorbet Benefits',
    logo: '/img/partners/sorbet.png',
    type: 'Earned Wage Access',
    features: [
      'Access the cash value of your unused PTO',
      'Low monthly interest payments with flexible repayment',
      'Income and expense tracking tools',
    ],
    amount: '$300-$3,000',
    monthly_payment: 'Small interest fee',
    fast_funding_fee: '5% of advance',
    apy: '9.99-36%',
    disclaimer: (
      <div className="space-y-2">
        <p>
          Sorbet is not a bank. Loans are originated by Lead bank at 9.99-36%
          APR and are subject to approval based partially on Paid Time Off (PTO)
          factors. Repayment plans include a balloon payment due at loan term or
          upon separation of employment. Must be 18 years old or age of majority
          in your home state to apply.
        </p>
        <p>
          For more information please see Sorbet Terms of Use:{' '}
          <Link
            className="text-blueberry-700"
            target="_blank"
            href="https://www.getsorbet.com/website-terms-of-use"
          >
            https://www.getsorbet.com/website-terms-of-use
          </Link>
          .
        </p>
      </div>
    ),
  },
  'compare-cash-advance-atm': {
    ...partnerATMCashAdvance,
    logo: '/img/partners/atm-landscape.png',
  },
  'compare-cash-advance-brigit': {
    ...partnerBrigitCashAdvance,
    logo: '/img/partners/brigit-landscape.png',
  },
  'compare-cash-advance-current': {
    ...partnerCurrentCashAdvance,
    logo: '/img/partners/current-landscape.png',
  },
  'compare-cash-advance-klover': {
    ...partnerKloverCashAdvance,
    logo: '/img/partners/klover-landscape.png',
  },
  'compare-cash-advance-wisely': {
    ...partnerWiselyCashAdvance,
    logo: '/img/partners/wisely-landscape-2.png',
  },
  'budgeting-apps-atm': {
    ...partnerATMBudgeting,
  },
  'budgeting-apps-klover': {
    ...partnerKloverBudgeting,
  },
  'budgeting-apps-monarchmoney': {
    ...partnerMonarchMoneyBudgeting,
  },
  'budgeting-apps-hiatus': {
    ...partnerHiatusBudgeting,
  },
  'budgeting-apps-rocketmoney': {
    name: 'Rocket Money',
    title: 'Rocket Money Benefits',
    logo: '/img/partners/rocketmoney.jpg',
    monthly_payment: '$6-$12/mo',
    features: [
      'Track and manage all subscriptions in one place',
      'Cancel unwanted subscriptions with a single tap',
      'Negotiate bills to potentially save hundreds annually',
      'Access unlimited budgets and full credit reports',
      'Monitor net worth and gain spending insights',
    ],
  },
  'budgeting-apps-ynab': {
    name: 'YNAB',
    title: 'YNAB Benefits',
    logo: '/img/partners/ynab.jpg',
    monthly_payment: '$14.99/mo',
    annual_payment: '$109.00/yr',
    features: [
      'Zero-based budgeting method for mindful spending',
      'Real-time syncing across multiple devices',
      'Goal tracking and detailed financial reports',
      'Educational resources to enhance financial literacy',
      'Share your subscription with up to five others',
    ],
  },
  'budgeting-apps-pocketguard': {
    name: 'PocketGuard',
    title: 'PocketGuard Benefits',
    logo: '/img/partners/pocketguard.jpg',
    monthly_payment: '$12.99/mo',
    annual_payment: '$74.99/yr',
    features: [
      'Easily track spending and set savings goals',
      '"In My Pocket" feature shows available disposable income',
      'Automatically categorizes expenses for better management',
      'Custom categories and transaction splitting with PocketGuard Plus',
      'Unlimited financial accounts for comprehensive tracking',
    ],
  },
  'compare-budgeting-atm': {
    ...partnerATMBudgeting,
    logo: '/img/partners/atm-landscape.png',
  },
  'compare-budgeting-klover': {
    ...partnerKloverBudgeting,
    logo: '/img/partners/klover-landscape.png',
  },
  'compare-budgeting-monarchmoney': {
    ...partnerMonarchMoneyBudgeting,
    logo: '/img/partners/monarchmoney-landscape.png',
  },
  'compare-budgeting-hiatus': {
    ...partnerHiatusBudgeting,
    logo: '/img/partners/hiatus-landscape.png',
  },
  'compare-credit-builder-account-self': {
    name: 'Self',
    title: 'Self Benefits',
    logo: '/img/partners/self-landscape.png',
    height: '32px',
    width: '72px',
    type: 'Installment Credit',
    callouts: [
      'Build credit while saving money',
      'No credit score required to start',
    ],
    amount: '$520-$1,800',
    term: '24 months',
    avg_score_increase: '49 points',
    monthly_payment: '$25+/mo',
    description: (
      <div className="space-y-2">
        <p>
          Build credit while building savings, save money and bump your credit
          score with a Self plan! Self makes it more convenient than ever to
          build credit and build savings.
        </p>
        <p>How it works:</p>
        <p>
          <strong>
            1. Open a Credit Builder Account* that is held by Self's bank
            partners.
          </strong>
          <br />
          Your money is secured and protected in a bank account.
        </p>
        <p>
          <strong>2. Pay off the loan in 24 months.</strong>
          <br />
          Each on-time payment builds credit history with all three credit
          bureaus and adds to your savings.
        </p>
        <p>
          <strong>
            3. Get money back at the end after building credit history.
          </strong>
          <br />
          Your paid off loan unlocks your CD and you get the money back, minus
          interest and fees.
        </p>
        <p>
          No credit score is needed to get started! Open a Credit Builder
          Account with Self. Start saving and building credit today!
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>¹ Minus interest and fees</p>
        <p>
          ² $25/mo, 24 mos, 15.92% APR; $35/mo, 24 mos, 15.69% APR; $48/mo, 24
          mos, 15.51% APR; $150/mo, 24 mos, 15.82% APR. See{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://self.inc/pricing"
          >
            self.inc/pricing
          </Link>
        </p>
        <p>
          ³ Credit Builder Account — proceeds are held in a deposit account
          until maturity
        </p>
        <p>
          ⁴ Results are not guaranteed. Improvement in your credit score is
          dependent on your specific situation and financial behavior. Failure
          to make monthly minimum payments by the payment due date each month
          may result in delinquent payment reporting to credit bureaus which may
          negatively impact your credit score. This product will not remove
          negative credit history from your credit report.
        </p>
        <p>
          † Review provided by customer to Self Financial Inc. directly. The
          comment above is related to individual experiences or results. Your
          results may vary.
        </p>
        <p>
          Credit Builder Accounts & Certificates of Deposit made/held by Lead
          Bank, Sunrise Banks, N.A., SouthState Bank, N.A., First Century Bank,
          N.A., each Member FDIC. Subject to credit approval.
        </p>
      </div>
    ),
  },
  'compare-credit-builder-account-creditstrong-magnum': {
    name: 'Credit Strong Magnum',
    title: 'Credit Strong Magnum Benefits',
    logo: '/img/partners/creditstrong-magnum-landscape.png',
    height: '32px',
    width: '132px',
    type: 'Installment Credit',
    callouts: [
      'Maximize credit growth and build savings',
      'Build $2,000 to $30,000 of credit history',
    ],
    amount: '$2,000-$30,000',
    term: '42-120 months',
    avg_score_increase: '86 points',
    monthly_payment: '$30+/mo',
    description: (
      <div className="space-y-2">
        <p>
          Build an average* of 86 points to your FICO®score with the biggest
          credit builder in the nation.
        </p>
        <p>
          Not everyone needs MAGNUM-sized accounts, but they may be right for
          you if you want to:
        </p>
        <ul className="list-disc pl-5">
          <li>Unlock higher credit limit credit cards</li>
          <li>Access higher-value personal loans</li>
          <li>Maximize your potential credit score increase</li>
        </ul>
        <p>Here's how it works:</p>
        <p>
          <strong>1. Pick a plan</strong>
          <br />
          MAGNUM builds $2,000-$30,000, of credit history starting at just $30 a
          month
        </p>
        <p>
          <strong>2. Pay off the loan every month</strong>
          <br />
          MAGNUM is the longest and largest credit builder in the nation,
          building up to 10 years of payment history with no prepayment or
          cancellation fees.
        </p>
        <p>
          <strong>
            3. Get your principal back after building big credit history
          </strong>
          <br />
          After your loan is paid off or canceled, you receive your payments
          back, minus any interest or fees.
        </p>
        <p>
          There's no hard credit check or credit score minimum to get started
          with MAGNUM. MAGNUM is for when you have cash but not credit, and
          other CreditStrong builders are better if your intent is to accrue
          savings.
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          *Average credit score improvement for MAGNUM account holders that made
          12 on-time payments. Additional{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://magnum.bank/terms-and-conditions/"
          >
            terms and conditions
          </Link>{' '}
          may apply.
        </p>
        <p>
          MAGNUM: With a $30 monthly payment, a loan of $2000 for 120 months at
          an annual percentage rate of 13.44% would yield a total payout of
          $2000 at the end of the term.
        </p>
        <p>
          CreditStrong® and MAGNUM® are registered trademarks of Austin
          Capital Bank SSB. FICO® is a registered Trademark of Fair Isaac
          Corporation. Equifax® and Paynet® are registered trademarks of
          Equifax, Inc.
        </p>
        <p>Austin Capital Bank SSB. Member FDIC.</p>
      </div>
    ),
  },
  'compare-credit-builder-account-creditstrong-revolv': {
    name: 'Credit Strong Revolv',
    title: 'Credit Strong Revolv Benefits',
    logo: '/img/partners/creditstrong-revolv-landscape.png',
    height: '32px',
    width: '120px',
    type: 'Revolving Credit',
    callouts: ['Reduce credit utilization fast', 'No hard credit checks'],
    amount: 'Up to $3,000',
    term: '1+ years',
    avg_score_increase: '62 points',
    monthly_payment: '$99/year',
    description: (
      <div className="space-y-2">
        <p>
          Build credit FAST without a credit card with an average* 62-point
          FICO®score increase.
        </p>
        <p>
          The fastest way to improve your credit score is to lower your credit
          utilization, and Revolv does just that.
        </p>
        <p>Here's how it works:</p>
        <p>
          <strong>1. Pick a plan</strong>
          <br />
          Quickly build $1,000 of revolving credit for just $99 a year.
        </p>
        <p>
          <strong>
            2. Add a monthly payment to supercharge your credit profile
          </strong>
          <br />
          100% of every monthly payment helps build your savings! Make on-time
          payments to unlock up to $3,000 of revolving credit!
        </p>
        <p>
          <strong>3. Unlock your savings at the end of the term</strong>
          <br />
          All of those monthly savings payments are now yours!
        </p>
        <p>
          There's no hard credit check or credit score minimum to start building
          or saving with Revolv!
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          *Average FICO® Score 8 increase of 62 points for Revolv account
          holders with a starting credit score under 550 who opened an account
          in Q1 2023 and made optional monthly savings payments between $15 and
          $45. Additional{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.creditstrong.com/terms-and-conditions/"
          >
            terms and conditions
          </Link>{' '}
          may apply.
        </p>
        <p>
          CreditStrong® and Revolv® are registered trademarks of Austin
          Capital Bank SSB. FICO® is a registered Trademark of Fair Isaac
          Corporation. Equifax® and Paynet® are registered trademarks of
          Equifax, Inc.
        </p>
        <p>Austin Capital Bank SSB. Member FDIC.</p>
      </div>
    ),
  },
  'compare-credit-builder-account-creditstrong-instal': {
    name: 'Credit Strong Instal',
    title: 'Credit Strong Instal Benefits',
    logo: '/img/partners/creditstrong-instal-landscape.png',
    height: '32px',
    width: '120px',
    type: 'Installment Credit',
    callouts: [
      'Start small and build credit + savings',
      'Flexible terms up to 48 months',
    ],
    amount: 'Up to $1,100',
    term: '24-48 months',
    avg_score_increase: '45 points',
    monthly_payment: '$28+/mo',
    description: (
      <div className="space-y-2">
        <p>
          Start building credit and savings at the lowest price with an average*
          45-point FICO®score increase.
        </p>
        <p>
          Choose among installment credit builders on your budget, building the
          same type of credit you would get from a mortgage or auto loan.
        </p>
        <p>Here's how it works:</p>
        <p>
          <strong>1. Pick a plan</strong>
          <br />
          Build up to $1,100 of installment credit with every payment reported
          to the three credit bureaus.
        </p>
        <p>
          <strong>2. Build credit + savings</strong>
          <br />
          Make on-time monthly payments and track your FICO®score from your
          CreditStrong dashboard.
        </p>
        <p>
          <strong>3. Unlock your savings at the end of the term</strong>
          <br />
          All of those monthly savings payments are now yours!
        </p>
        <p>
          There's no hard credit check or credit score minimum to start building
          or saving with Instal!
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          *Average FICO® Score 8 increase for Instal/CS Max account holders
          with a starting credit score under 550 who opened an account in Q1
          2023 and made at least 3 on-time payments. No credit score increase is
          guaranteed. Individual results will vary. Every individual's credit
          profile is unique, and your personal credit score may be impacted by
          all accounts and activity reported on your credit report. Additional{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.creditstrong.com/terms-and-conditions/"
          >
            terms and conditions
          </Link>{' '}
          may apply.
        </p>
        <p>
          Instal: With a $28 monthly payment, a loan of $1,010 for 48 months at
          an annual percentage rate of 15.61% would yield a total payout of
          $1,010 at the end of the term.
        </p>
        <p>
          CreditStrong® and Instal® are registered trademarks of Austin
          Capital Bank SSB. FICO® is a registered Trademark of Fair Isaac
          Corporation. Equifax® and Paynet® are registered trademarks of
          Equifax, Inc.
        </p>
        <p>Austin Capital Bank SSB. Member FDIC.</p>
      </div>
    ),
  },
  'compare-investing-acorns': {
    name: 'Acorns',
    title: 'Acorns Benefits',
    logo: '/img/partners/acorns-landscape.png',
    height: '32px',
    width: '126px',
    type: 'Automated Investing',
    features: [
      'Passive investing with round-ups and automatic recurring contributions',
      'Diversified portfolios and retirement accounts',
    ],
    callouts: ['Spare change round-ups', '$20 bonus for new users'],
    monthly_payment: '$3-$12/mo',
    minimum_deposit: '$5',
    account_types: ['Individual', 'Retirement', 'Custodial'],
    best_for: 'Beginners seeking hands-off investing',
    description: (
      <div className="space-y-2">
        <p>
          Start investing effortlessly with Acorns, the app that grows your
          wealth automatically. With spare change round-ups, diversified
          portfolios, and flexible contributions, Acorns makes investing simple.
          Sign up today and invest just $5 to claim a $20 bonus!
        </p>
        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          Investing involves risk, including loss of principal. Please consider,
          among other important factors, your investment objectives, risk
          tolerance and Acorns’ pricing before investing. Acorns Subscription
          Fees are assessed based on the tier of services in which you are
          enrolled, view details in our program agreement. Investment advisory
          services offered by Acorns Advisers, LLC (Acorns), an SEC-registered
          investment advisor. Brokerage services are provided to clients of
          Acorns by Acorns Securities, LLC, an SEC-registered broker-dealer and
          member FINRA/SIPC.
        </p>
        <p>
          Diversification and asset allocation do not guarantee a profit, nor do
          they eliminate the risk of loss of principle. Compounding is the
          process in which an asset's earnings are reinvested to generate
          additional earnings over time. It does not ensure positive performance
          nor does it protect against loss. Acorns clients may not experience
          compound returns and investment results will vary based on market
          volatility and fluctuating prices.
        </p>
        <p>
          Spare change invested with Round-Ups® is transferred from your linked
          funding source to your Acorns Invest account, where the funds are
          invested into a portfolio of selected ETFs. If you do not maintain an
          adequate amount of funds in your funding source sufficient to cover
          your Round-Ups® investment, you could incur overdraft fees with your
          financial institution. Round-Up investments from an external account,
          will be processed when your Pending Round-Ups reach or exceed $5.
        </p>
        <p>
          The average Acorns investor, with a verified and active account, has
          invested $166 between 1/1/21 and 4/30/21 using the Round-Ups®
          feature. Individual results will vary and averages only take into
          account the Round-Ups® investments themselves and do not reflect the
          impact investment volatility will have on overall performance.
        </p>
        <p>
          Acorns Invest is an individual investment account. Acorns Later is an
          Individual Retirement Account (either Traditional, ROTH or SEP IRA)
          selected for clients based on their answers to a suitability
          questionnaire. Invest and Later recommend a portfolio of ETFs
          (exchange traded funds) to clients, please note that a properly
          suggested portfolio recommendation is dependent upon current and
          accurate financial and risk profiles.
        </p>
        <p>
          Acorns is not a bank. Acorns Visa™ debit cards and banking services
          are issued by Lincoln Savings Bank or nbkc bank, members FDIC. Acorns
          Checking clients are not charged overdraft fees, maintenance fees, or
          ATM fees for cash withdrawals from in-network ATMs.
        </p>
        <p>
          Acorns Earn provides subscribers access to shop with our partners and
          earn bonus investments into your Acorns Invest portfolios when
          purchasing items from the partner brands. Acorns Earn rewards
          investments are made by Acorns Grow, Inc. into your Acorns Invest
          account through a partnership Acorns Grow maintains with each Acorns
          Earn partner.
        </p>
        <p>
          Acorns, Round-Ups® investments, Real-Time Round-Ups® investments,
          Invest the Change and the Acorns logo are registered trademarks of
          Acorns Grow Incorporated. All product and company names are
          trademarks™ or registered® trademarks of their respective holders.
          Use of them does not imply any affiliation with or endorsement by
          them.
        </p>
        <p>
          For additional important risks, disclosures, and information, please
          visit{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.acorns.com/terms/"
          >
            https://www.acorns.com/terms/
          </Link>
        </p>
      </div>
    ),
  },
  'compare-investing-stash': {
    name: 'Stash',
    title: 'Stash Benefits',
    logo: '/img/partners/stash-landscape.png',
    height: '32px',
    width: '143px',
    type: 'Self-Directed and Automated',
    features: [
      'Flexibility with self-directed and automated investing options',
      'Stock-Back® rewards offer a unique way to earn fractional shares through everyday spending',
    ],
    callouts: ['Beginner-friendly tools', '$10 bonus for new users'],
    monthly_payment: '$3-$9/mo',
    minimum_deposit: '$5',
    account_types: ['Individual', 'Retirement', 'Custodial'],
    best_for: 'Beginners wanting to learn and invest',
    description: (
      <div className="space-y-2">
        <p>
          Are you new to the stock market? Invest with Stash, the investing app
          helping millions of Americans build a better life and fast track their
          safety net. Sign up today and invest just $5 and receive a $10 bonus,
          plus get a 7-day trial.
        </p>
        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          Investment advisory services offered by Stash Investments LLC, an SEC
          registered investment adviser. Investing involves risk and investments
          may lose value. Holdings and performance are hypothetical. Offer is
          subject to{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://lp.stash.com/disclosures/5-first-deposit-promotion-terms-and-conditions/"
          >
            T&Cs
          </Link>
          .
        </p>
      </div>
    ),
  },
  'compare-investing-moomoo': {
    name: 'moomoo',
    title: 'moomoo Benefits',
    logo: '/img/partners/moomoo-landscape.png',
    height: '32px',
    width: '128px',
    type: 'Active Trading',
    features: [
      'Targeted at experienced traders with sophisticated options strategies',
      'Live insights and paper trading',
      'Competitive high-yield cash management and commission-free trading',
    ],
    callouts: [
      'Advanced tools',
      '8.1% APY*',
      'Up to 15 free stocks¹ for new users',
    ],
    monthly_payment: '$0 commission*',
    minimum_deposit: '$0',
    account_types: ['Individual', 'Retirement'],
    best_for: 'Advanced traders and commission-free trading',
    description: (
      <div className="space-y-2">
        <p>
          Enjoy 8.1% APY* on uninvested cash for a limited-time and up to 15
          free stocks¹ with a qualified deposit.
        </p>
        <p>
          Make unlimited commission-free trades in Stocks, ADRs, ETFs, & Options
          with moomoo®. $0 Commission for trading Stocks, ADRs, ETFs, and
          Options. No minimum account balance.
        </p>
        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          *Promo 8.1% APY (as of 11/08/2024) is for new users who have never
          made a deposit or transfer before. The Base Rate is 4.35% APY, plus a
          3.75% temporary booster once activated. The actual APY earned may
          differ based on when the user activates the coupon, as the base rate
          may change.
        </p>
        <p>
          Cash Sweep 4.35% APY eligibility: All new user accounts. (Existing
          users must meet either qualifying deposit, transfer, or referral
          requirements to unlock promo rate.) Otherwise, the rate is 0.03%.
          Rates as of 11/08/2024 and subject to change. Interest is earned on
          uninvested cash swept from customer’s brokerage account to Deposit
          Banks in the Cash Sweep Program “Program Bank”. These banks pay
          interest on the swept cash minus any fees paid to Moomoo Financial
          Inc. (“MFI”). Once swept to a Program Bank, the customer’s uninvested
          cash becomes eligible for FDIC insurance up to $250,000 per Program
          Bank, inclusive of any other deposits the customer already hold at the
          bank, in the same ownership capacity, which may impact how much is
          covered. MFI is not responsible for monitoring the amount of each
          customer’s deposits in any Program Bank, whether in the cash sweep
          program or otherwise, to determine whether the amount on deposit
          exceeds the FDIC insurance limits. Note, until funds are swept to a
          Program Bank, they are held in the customer’s brokerage account which
          is protected by SIPC. Once funds are swept, they are no longer in the
          brokerage account and not protected by SIPC. However, they become
          eligible for FDIC insurance through the Program Banks. See the Deposit
          Bank List for the current listing of Program Banks. The Cash Sweep
          program is a feature of the brokerage account. Neither MFI, nor any of
          its affiliates are banks. Moomoo is a financial information and
          trading app offered by Moomoo Technologies Inc. In the U.S.,
          investment products and services on Moomoo are offered by Moomoo
          Financial Inc., Member FINRA/SIPC. Additional terms & conditions
          apply. For more info, visit:{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://moomoo.com/us/support/topic4_445"
          >
            https://moomoo.com/us/support/topic4_445
          </Link>
        </p>
        <p>
          APY Booster Rate is effective for 3 months on up to $20,000 in cash
          sweep program once coupon is activated. Terms & Conditions apply. See:{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.moomoo.com/us/support/topic4_410"
          >
            https://www.moomoo.com/us/support/topic4_410
          </Link>
        </p>
        <p>
          ¹The deposit bonus is available only to clients who have never made a
          deposit or transfer before 5/29/2024. Offer is only valid for US
          residents with a valid SSN who are at least 18 years old. After
          receiving the free stocks, you will need to maintain your average
          assets based on the requirements for 60 days to unlock the stocks.
          Additional terms and conditions apply, learn more at{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.momoo.com/us/support/topic4_410"
          >
            https://www.momoo.com/us/support/topic4_410
          </Link>
        </p>
        <p>
          ² ACAT transfer in from another account into Moomoo and receive a Cash
          Reward of up to $300. This is a 1.5% match on your first $20k
          transferred in. These assets must remain in the account for 90 days
          before the Cash Reward is unlocked. Cash rewards, redeemable solely
          through the moomoo app, represent potential credits for eligible
          equity purchases in Moomoo Financial Inc. brokerage account and hold
          no other value. Additional terms & conditions apply, learn more at:{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.moomoo.com/us/support/topic4_410"
          >
            https://www.moomoo.com/us/support/topic4_410
          </Link>
        </p>
        <p>
          ³ $0 commission trading is available only to U.S. residents trading in
          the U.S. markets through Moomoo Financial Inc. Other fees may apply,
          for more information, visit{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://moomoo.com/us/pricing"
          >
            https://moomoo.com/us/pricing
          </Link>
        </p>
        <p>
          Options trading entails significant risk and is not appropriate for
          all investors. Certain complex options strategies carry additional
          risk. It is important that investors read Characteristics and Risks of
          Standardized Options (
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://j.us.moomoo.com/00xBBz"
          >
            https://j.us.moomoo.com/00xBBz
          </Link>
          ) before engaging in any options trading strategies.
        </p>
        <p>
          ⁴ Margin trading entails greater risk, including risk of loss and
          incurrence of margin interest debt, and is not suitable for all
          investors. Please assess your financial circumstances and risk
          tolerance before trading on margin. The Margin Rate is subject to
          change without notice. See{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.moomoo.com/us"
          >
            https://www.moomoo.com/us
          </Link>{' '}
          for the most current rates.
        </p>
        <p>
          ⁵ 5.1% APY eligibility: **5.1% APY eligibility: This rate is only for
          new & existing users that meet either a qualifying deposit, transfer,
          or referral requirements. Promo rate is as of [11/9/23].{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.moomoo.com/us/support/topic4_445"
          >
            Terms and Conditions Apply
          </Link>
          . The Cash Sweep Program is a feature of the brokerage account. The
          APY rate is subject to change. Neither Moomoo Financial Inc. nor any
          of its affiliates are banks.
        </p>
        <p>
          ⁶ Level 2 data (NYSE ArcaBook) is free for Moomoo Financial Inc.
          accounts with a minimum 30-day average account value of $100+. Other
          conditions apply, to learn more, visit{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://moomoo.com/us/support/topic3_435"
          >
            https://moomoo.com/us/support/topic3_435
          </Link>
        </p>
        <p>
          Moomoo is a financial information and trading app offered by Moomoo
          Technologies Inc. In the U.S., investment products and services on
          Moomoo are offered by Moomoo Financial Inc., Member FINRA/SIPC.
        </p>
      </div>
    ),
  },
  'compare-bank-account-chime': {
    name: 'Chime',
    title: 'Chime Benefits',
    logo: '/img/partners/chime-landscape.png',
    height: '32px',
    width: '112px',
    type: 'Digital Checking',
    features: [
      'Early direct deposit (up to 2 days early with direct deposit)',
      '47,000+ fee-free ATMs',
      'SpotMe overdraft protection up to $200',
    ],
    callouts: ['No monthly fees', 'No overdraft fees', 'Early direct deposit'],
    best_for: 'Budget-conscious users avoiding fees',
    monthly_payment: '$0',
    minimum_deposit: '$0',
    minimum_balance: 'None',
    overdraft_fee: 'None',
    description: (
      <div className="space-y-2">
        <p>
          Chime makes banking easy with no monthly fees, no overdraft fees, and
          no minimum deposit requirements. With features like early direct
          deposit, SpotMe® overdraft protection, and access to 47,000+ fee-free
          ATMs, Chime helps you manage your money with confidence and
          transparency.
        </p>
        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          Banking: Chime is a financial technology company, not a bank. Banking
          services provided by The Bancorp Bank, N.A. or Stride Bank, N.A.,
          Members FDIC.
        </p>
        <p>
          * SpotMe/Fee-Free Overdraft SpotMe® for Credit Builder is an
          optional, no interest/no fee overdraft line of credit tied to the
          Secured Deposit Account. SpotMe on Debit is an optional, no fee
          service attached to your Chime Checking Account (individually or
          collectively, “SpotMe”). Eligibility for SpotMe requires $200 or more
          in qualifying direct deposits to your Chime Checking Account each
          month.
        </p>
        <p>
          Qualifying members will be allowed to overdraw their Chime Checking
          Account and/or their Secured Deposit Account up to $20 in total, but
          may be later eligible for a higher combined limit of up to $200 or
          more based on member’s Chime account history, direct deposit frequency
          and amount, spending activity and other risk-based factors. Your
          SpotMe Limit will be displayed to you within the Chime mobile app. You
          will receive notice of any changes to your SpotMe Limit. Your SpotMe
          Limit may change at any time, at Chime or its banking partners’
          discretion. Although there are no overdraft fees, there may be
          out-of-network or third party fees associated with ATM transactions or
          OTC cash withdrawal fees at retailers. SpotMe® won't cover non card
          transactions, including ACH transfers, Pay Anyone transfers, or Chime
          Checkbook transactions. SpotMe Terms and Conditions.
        </p>
        <p>
          SpotMe® Boost: Boosts are available to eligible Chime members
          enrolled in SpotMe® and are subject to monthly limits.
        </p>
        <p>
          Early Pay: Early access to direct deposit funds depends on the timing
          of the submission of the payment file from the payer. We generally
          make these funds available on the day the payment file is received,
          which may be up to 2 days earlier than the scheduled payment date.
        </p>
        <p>
          ~ Fee-free ATMs Out-of-network ATM withdrawal and over the counter
          advance fees may apply except at MoneyPass ATMs in a 7-Eleven, or any
          Allpoint or Visa Plus Alliance ATM.
        </p>
        <p>
          Chime Card: To apply for a Chime Card, a Chime® Checking Account is
          required.
        </p>
        <p>
          Credit Score: Based on a representative study conducted by Experian®
          in January 2024 members who made their first purchase with Credit
          Builder, an earlier version of Chime Card, between June 2022 and
          October 2022 observed an average FICO® Score 8 increase of 30 points
          after approximately 8 months. A credit score improvement is not
          guaranteed. On-time payment history can have a positive impact on your
          credit score. Late payment may negatively impact your credit score.
          Activity on other credit accounts may also impact your score.
        </p>
        <p>
          Security Deposit: Money added to Chime Card™ will be held in a
          secured deposit account as collateral for your Chime Card, and you can
          spend up to this amount. You can use money deposited in your Secured
          Deposit Account to pay off your charges at the end of every month.
        </p>
        <p>
          See{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.chime.com/policies/bancorp/deposit-account-agreement/"
          >
            Terms and Conditions
          </Link>{' '}
          for details.
        </p>
      </div>
    ),
  },
  'compare-bank-account-walmartmoneycard': {
    name: 'Walmart MoneyCard',
    title: 'Walmart MoneyCard Benefits',
    logo: '/img/partners/walmart-moneycard-landscape.png',
    height: '32px',
    width: '96px',
    type: 'Prepaid Debit Card',
    features: [
      '3% cash back on Walmart.com',
      '2% cash back on Walmart fuel',
      '1% cash back in stores',
      'Family accounts with up to 4 cards',
      '2% APY savings',
      'Early direct deposit',
    ],
    callouts: ['Earn cash back on Walmart purchases', 'Early direct deposit'],
    best_for: 'Frequent Walmart shoppers',
    monthly_payment: '$0 or $5.94/mo',
    minimum_deposit: '$0',
    minimum_balance: 'None',
    overdraft_fee: 'None or up to $15',
    description: (
      <div className="space-y-2">
        <p>
          Walmart MoneyCard offers cash back rewards for shopping at Walmart,
          early direct deposit, and a 2% APY savings feature. With family
          account options and overdraft protection, it’s an excellent choice for
          frequent Walmart shoppers seeking financial perks and convenience.
        </p>
        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          Must be 18 or older to purchase a Walmart MoneyCard. Activation
          requires online access and identity verification (including SSN) to
          open an account. Mobile or email verification and mobile app are
          required to access all features.
        </p>
        <p>
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.walmartmoneycard.com/legal-info"
          >
            See Account Agreement for fees, terms and conditions.
          </Link>
        </p>
        <p>
          ¹ Cash back, up to $75 per year, is credited to card balance at end of
          reward year and is subject to successful activation and other
          eligibility requirements. Redeem rewards using our website or app. You
          will earn cash back of three percent (3%) on qualifying purchases made
          at Walmart.com and in the Walmart app using your card or your card
          number, two percent (2%) at Walmart fuel stations, and one percent
          (1%) on qualifying purchases at Walmart stores in the United States
          (less returns and credits) posted to your Card during each reward
          year. Grocery delivery and pickup purchases made on Walmart.com or the
          Walmart app earn 1%. For the purposes of cash back rewards, a "reward
          year" is twelve (12) monthly periods in which you have paid your
          monthly fee or had it waived. See{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.walmartmoneycard.com/content/dam/walmart-moneycard/legal/deposit-account-agreement/wmmc-daa-od.pdf"
          >
            Deposit Account Agreement
          </Link>{' '}
          for details.​
        </p>
        <p>
          ² Opt-in required. Account must have initial eligible direct deposits,
          must be in good standing and have an activated chip-enabled debit card
          to opt-in. Ongoing eligible direct deposits and other criteria apply
          to maintain eligibility for overdraft protection. Only debit card
          purchase transactions are eligible for overdraft protection and
          overdrafts are paid at our discretion. We reserve the right to not pay
          overdrafts. For example, we may not pay overdrafts if the account is
          not in good standing, or is not receiving ongoing eligible direct
          deposits, or has too many overdrafts. Overdraft fees may cause your
          account to be overdrawn by an amount that is greater than your
          overdraft coverage. A $15 fee may apply to each eligible purchase
          transaction that brings your account negative. Balance must be brought
          to at least $0 within 24 hours of authorization of the first
          transaction that overdraws your account to avoid a fee. Overdraft
          protection is only available on Demand Deposit Accounts that meet
          eligibility requirements. Log into and refer to your Account Agreement
          to check feature availability.
        </p>
        <p>
          ³ Direct Deposit: Early availability of direct deposit depends on
          timing of payroll's payment instructions and fraud prevention
          restrictions may apply. As such, the availability or timing of early
          direct deposit may vary from pay period to pay period. Make sure the
          name and social security number on file with your employer or benefits
          provider matches what's on your Walmart MoneyCard account exactly. We
          will not be able to deposit your payment if we are unable to match
          recipients.
        </p>
        <p>
          ⁴ Interest is paid annually on each enrollment anniversary based on
          the average daily balance of the prior 365 days, up to a maximum
          average daily balance of $1,000, if the account is in good standing
          and has a positive balance. 2.00% Annual Percentage Yield may change
          at any time before or after account is opened. Annual Percentage Yield
          is accurate as of September 2024. Fees on your primary account may
          reduce earnings on your savings account.
        </p>
        <p>
          ⁵ Family Accounts: Activated, personalized card required. Other fees
          apply to the additional account. Family members age 13 years and over
          are eligible. Limit 4 cards per account. See{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.walmartmoneycard.com/content/dam/walmart-moneycard/legal/deposit-account-agreement/wmmc-daa-od.pdf"
          >
            Deposit Account Agreement
          </Link>{' '}
          for details.
        </p>
        <p>
          Walmart MoneyCards are issued by Green Dot Bank, Member FDIC, pursuant
          to a license from Visa U.S.A., Inc. Visa is a registered trademark of
          Visa International Service Association. And by Mastercard
          International Inc. Mastercard and the circles design are registered
          trademarks of Mastercard International Incorporated.
        </p>
        <p>
          Green Dot Bank also operates under the following registered trade
          names: GO2bank, GoBank and Bonneville Bank. All of these registered
          trade names are used by, and refer to, a single FDIC-insured bank,
          Green Dot Bank. Deposits under any of these trade names are deposits
          with Green Dot Bank and are aggregated for deposit insurance coverage.
          Green Dot is a registered trademark of Green Dot Corporation.
        </p>
        <p>
          The "Spark" design ( * ), Walmart and Save money. Live better are
          marks and/or registered marks of Walmart, Inc.
        </p>
        <p>©2024 Walmart Apollo, LLC. All rights reserved.</p>
      </div>
    ),
  },
  'compare-bank-account-mph': {
    name: 'MPH Bank',
    title: 'MPH Bank Benefits',
    logo: '/img/partners/mph-bank-landscape.png',
    height: '32px',
    width: '112px',
    type: 'High-Yield Digital Checking',
    features: [
      '5.00% APY on deposits up to $50K (with $2K direct deposit)',
      '55K+ fee-free ATMs',
      'Unlimited savings goals at 3% APY',
      'Early direct deposit',
    ],
    callouts: ['5.00% APY* interest', 'No fees period', 'Early direct deposit'],
    best_for: 'Savers seeking high-interest checking',
    monthly_payment: '$0',
    minimum_deposit: '$0',
    minimum_balance: 'None',
    overdraft_fee: 'None',
    description: (
      <div className="space-y-2">
        <p>
          MPH Bank combines high-yield checking with no fees to deliver a
          seamless digital banking experience. Earn up to 5.00% APY on deposits,
          set unlimited savings goals with 3.00% APY, and access 55K+ fee-free
          ATMs nationwide. Perfect for savers looking to maximize their
          earnings.
        </p>

        <p>
          <strong>Key Features</strong>
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-2">
        <p>
          * Free accounts earn 5.00%APY (APY stands for Annual Percentage Yield)
          up to $50,000 in account deposits. Requires $2,000+ monthly direct
          deposit to qualify. All deposits in your checking account over $50,000
          will receive 0.00%APY. All deposit products are provided by, and
          mph.bank is a brand of, Liberty Savings Bank, F.S.B. Member FDIC.
        </p>
        <p>
          1 Goals accounts earn 3.00%APY (APY stands for Annual Percentage
          Yield). All deposit products are provided by, and mph.bank is a brand
          of, Liberty Savings Bank, F.S.B. Member FDIC.
        </p>
        <p>
          2 Access to funds for getting paid early can be up to two days prior
          to the date of scheduled payment and is dependent on the submission of
          direct deposit by the payer. Standard fraud prevention restrictions
          apply.
        </p>
        <p>
          3 Zelle® and the Zelle® related marks are wholly owned by Early
          Warning Services, LLC and are used herein under license.
        </p>
        <p>
          4 Click on the link to locate the 55,000+ surcharge-FREE ATMs in the{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://www.allpointnetwork.com/locator.html"
          >
            ALLPOINT NETWORK
          </Link>{' '}
          near you.
        </p>
        <p>
          See full{' '}
          <Link
            target="_blank"
            className="text-blueberry-700"
            href="https://smile.mph.bank/terms-and-conditions"
          >
            terms and conditions
          </Link>{' '}
          for details.
        </p>
      </div>
    ),
  },
}

export const RecommendedOffer = ({
  className,
  ctaUrl,
  partnerName = 'surveys-inboxdollars',
  title,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  return (
    <div
      className={clsx(
        'shadow-blueberry-900/20 border-blueberry-1000 not-prose relative mb-12 mt-8 rounded-2xl border-4 bg-white shadow-xl',
        className,
      )}
    >
      {title && (
        <h2
          id={title.toLowerCase().replace(/ /g, '-')}
          className="bg-blueberry-1000 m-0 rounded-t-lg p-2 text-center text-xl font-bold text-white"
        >
          <Link href={ctaUrl} target="_blank">
            {title}
          </Link>
        </h2>
      )}
      <div className="flex flex-col items-center justify-between gap-4 px-4 pt-4 sm:flex-row sm:items-start sm:gap-8 sm:px-8 sm:pt-8">
        {data.logo && (
          <div className="w-1/2 max-w-[110px]">
            <Link href={ctaUrl} target="_blank">
              <img src={data.logo} alt={data.name} />
            </Link>
          </div>
        )}
        <div className="flex-1">
          <h2 className="text-lg font-bold">{data.title}</h2>
          {data.features && (
            <ul className="list-disc pl-4">
              {data.features.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          )}
          {(data.amount ||
            data.monthly_payment ||
            data.fast_funding_fee ||
            data.disclaimer) && (
            <>
              <h2 className="text-md mt-2 font-bold">Offer Details</h2>
              <ul className="list-disc pl-4">
                {data.amount && (
                  <li>
                    <span className="text-gray-500">Advance Amount:</span>{' '}
                    {data.amount}
                  </li>
                )}
                {data.monthly_payment && (
                  <li>
                    <span className="text-gray-500">Monthly Payment:</span>{' '}
                    {data.monthly_payment}
                  </li>
                )}
                {data.annual_payment && (
                  <li>
                    <span className="text-gray-500">Annual Payment:</span>{' '}
                    {data.annual_payment}
                  </li>
                )}
                {data.fast_funding_fee && (
                  <li>
                    <span className="text-gray-500">Fast Funding Fee:</span>{' '}
                    {data.fast_funding_fee}
                  </li>
                )}
                {data.disclaimer && (
                  <li>
                    <Popover className="relative">
                      <span className="text-gray-500">Disclaimers:</span>{' '}
                      <PopoverButton
                        as="button"
                        className="inline-block text-gray-500 underline focus:outline-none"
                      >
                        More Info
                      </PopoverButton>
                      <PopoverPanel
                        anchor="top"
                        transition
                        className="shadow-dolphin-900/50 z-30 m-2 min-w-[300px] origin-top rounded-2xl border bg-white p-4 shadow-xl transition duration-300 ease-out data-[closed]:opacity-0 sm:w-[400px]"
                      >
                        <p className="px-4 pb-2 font-bold leading-6 text-gray-700">
                          Provider Disclaimer
                        </p>
                        <p className="px-4 leading-6 text-gray-700">
                          {data.disclaimer}
                        </p>
                      </PopoverPanel>
                    </Popover>
                  </li>
                )}
              </ul>
            </>
          )}
          <div className="mt-2 flex justify-start pb-4 sm:pb-8">
            <Link
              href={ctaUrl}
              target="_blank"
              className="text-blueberry-700 inline-block font-bold"
            >
              <span className="mr-2" role="img" aria-label="arrow">
                👉
              </span>
              <span className="underline">Go to {data.name}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

const CompareOffer = ({
  className,
  Logo,
  Comparison,
  Cta,
  type,
  callouts,
  DialogOpenButton,
  DialogLogo,
  DialogFeatures,
  DialogDescription,
  DialogCta,
  Disclaimer,
  isOpen,
  handleClose,
}: {
  className?: string
  Logo: React.ReactNode
  Comparison: React.ReactNode
  Cta: React.ReactNode
  type: string
  callouts?: string[]
  DialogOpenButton: React.ReactNode
  DialogLogo: React.ReactNode
  DialogFeatures?: string[]
  DialogDescription?: React.ReactNode
  DialogCta: React.ReactNode
  Disclaimer: React.ReactNode
  isOpen: boolean
  handleClose: () => void
}) => {
  const Callouts = callouts && (
    <div className="flex flex-row flex-wrap justify-center gap-2 px-4 sm:justify-start">
      {callouts.map((callout, index) => (
        <span
          key={index}
          className="bg-blueberry-50 text-blueberry-500/80 ring-blueberry-500/20 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium tracking-tight ring-1 ring-inset"
        >
          {callout}
        </span>
      ))}
    </div>
  )

  return (
    <div
      className={clsx(
        'rounded-xl border border-gray-300 bg-white shadow',
        className,
      )}
    >
      <div className="hidden sm:block sm:pt-4">{Callouts}</div>
      <div className="flex flex-col items-center justify-between p-4 sm:flex-row sm:gap-4 lg:gap-8">
        {Logo}
        <div className="pt-4 sm:hidden">{Callouts}</div>
        <div className="w-full flex-1 py-4 sm:w-auto sm:py-0">{Comparison}</div>
        <div className="w-full text-center sm:relative sm:-top-5 sm:w-auto">
          {Cta}
        </div>
      </div>
      <div className="rounded-b-xl bg-gray-100 px-2">
        <div className="flex flex-row items-center justify-between gap-2">
          <span className="p-2 text-sm font-semibold text-gray-500">
            {type}
          </span>
          {DialogOpenButton}
          <Dialog
            className="relative z-50 transition duration-300 ease-out data-[closed]:opacity-0"
            transition
            open={isOpen}
            onClose={handleClose}
          >
            <DialogBackdrop className="fixed inset-0 bg-black/30" />
            <div className="fixed inset-0 w-screen overflow-y-auto p-4">
              <div className="flex min-h-full items-center justify-center">
                <DialogPanel className="relative max-w-2xl rounded-2xl bg-white pb-8 shadow-xl">
                  <div className="flex flex-row items-start justify-between px-8 pb-4 pt-8">
                    <div className="h-auto">{DialogLogo}</div>
                    <DialogButton
                      as="button"
                      onClick={handleClose}
                      className="absolute right-5 top-3"
                    >
                      ✕
                    </DialogButton>
                  </div>
                  <div className="flex flex-col gap-2 px-8">
                    <DialogTitle className="font-bold">
                      Offer Details
                    </DialogTitle>
                    {DialogDescription}
                    {DialogFeatures && (
                      <ul className="list-disc pl-5">
                        {DialogFeatures.map((benefit, index) => (
                          <li key={index}>{benefit}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div
                    className={`flex justify-center px-8 py-4 ${!Disclaimer ? 'pb-0' : ''}`}
                  >
                    {DialogCta}
                  </div>
                  {Disclaimer && (
                    <div className="flex flex-col gap-2 px-8">
                      <DialogTitle className="font-bold">
                        Provider Disclaimer
                      </DialogTitle>
                      <div className="text-sm text-gray-500">{Disclaimer}</div>
                    </div>
                  )}
                </DialogPanel>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}

const ComparisonItemNoWrap = ({
  label,
  value,
}: {
  label: string
  value: string | React.ReactNode
}) => (
  <div className="flex flex-row items-center justify-between gap-2 sm:gap-2 md:flex-col md:gap-1 md:text-center">
    <span className="body-4 md:body-3 font-normal text-gray-500">{label}</span>
    <span className="body-3 lg:body-2 text-nowrap font-medium md:font-semibold">
      {value}
    </span>
  </div>
)

const ComparisonItemWrapLabelAbove = ({
  className,
  label,
  value,
}: {
  className?: string
  label: string
  value: string | React.ReactNode
}) => (
  <div
    className={clsx(
      'flex flex-col items-start justify-between gap-0 sm:gap-0 md:gap-1',
      className,
    )}
  >
    <span className="body-4 md:body-3 text-nowrap font-normal text-gray-500">
      {label}
    </span>
    <span className="body-3 lg:body-2 font-medium md:font-semibold">
      {value}
    </span>
  </div>
)

export const CompareOfferCashAdvance = ({
  className,
  ctaUrl,
  partnerName,
  trackEvent,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleDialog = (partnerName: string) => {
    const willOpen = !open[partnerName]

    // Track event when opening the dialog
    if (willOpen && trackEvent) {
      trackEvent('view_compare_cash_advance_more_info', {
        partner: data.name,
      })
    }

    setOpen((prevState) => ({
      ...prevState,
      [partnerName]: !prevState[partnerName],
    }))
  }

  const Logo = (
    <div style={{ height: data.height, width: data.width }}>
      <Link href={ctaUrl} target="_blank">
        <img className={`h-[32px] w-auto`} src={data.logo} alt={data.name} />
      </Link>
    </div>
  )

  const Comparison = (
    <div className="mx-auto grid grid-cols-1 items-start gap-2 sm:mx-auto sm:max-w-[350px] md:max-w-[500px] md:grid-cols-3 md:items-stretch md:gap-4 lg:mx-0 lg:max-w-none">
      {data.amount && (
        <ComparisonItemNoWrap label="Amount" value={data.amount} />
      )}
      {data.monthly_payment && (
        <ComparisonItemNoWrap
          label="Monthly Payment"
          value={data.monthly_payment}
        />
      )}
      {data.fast_funding_fee && (
        <ComparisonItemNoWrap
          label="Fast Funding Fee"
          value={data.fast_funding_fee}
        />
      )}
    </div>
  )

  const type = data.type || ''
  const callouts = data.callouts || []

  const Cta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const DialogOpenButton = (
    <DialogButton
      as="button"
      onClick={() => toggleDialog(partnerName)}
      className="inline-block whitespace-nowrap p-2 text-sm font-bold text-gray-500 underline hover:text-gray-600 focus:outline-none active:text-gray-800"
    >
      More Info
    </DialogButton>
  )

  const DialogLogo = (
    <img src={data.logo} alt={data.name} className="max-h-[30px]" />
  )

  const DialogDescription = data.description || <></>
  const DialogFeatures = data.features || []

  const DialogCta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full max-w-[275px] rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const Disclaimer = data.disclaimer || <></>
  const isOpen = open[partnerName] || false
  const handleClose = () => toggleDialog(partnerName)

  return (
    <CompareOffer
      className={className}
      Logo={Logo}
      Comparison={Comparison}
      Cta={Cta}
      type={type}
      callouts={callouts}
      DialogOpenButton={DialogOpenButton}
      DialogLogo={DialogLogo}
      DialogDescription={DialogDescription}
      DialogFeatures={DialogFeatures}
      DialogCta={DialogCta}
      Disclaimer={Disclaimer}
      isOpen={isOpen}
      handleClose={handleClose}
    />
  )
}

export const CompareOfferCreditBuilderAccount = ({
  className,
  ctaUrl,
  partnerName,
  trackEvent,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleDialog = (partnerName: string) => {
    const willOpen = !open[partnerName]

    // Track event when opening the dialog
    if (willOpen && trackEvent) {
      trackEvent('view_compare_credit_builder_account_more_info', {
        partner: data.name,
      })
    }

    setOpen((prevState) => ({
      ...prevState,
      [partnerName]: !prevState[partnerName],
    }))
  }

  const Logo = (
    <div style={{ height: data.height, width: data.width }}>
      <Link href={ctaUrl} target="_blank">
        <img className={`h-[32px] w-auto`} src={data.logo} alt={data.name} />
      </Link>
    </div>
  )

  const Comparison = (
    <div className="mx-auto grid grid-cols-1 items-start gap-2 sm:mx-auto sm:max-w-[350px] md:max-w-[500px] md:grid-cols-2 md:items-stretch md:gap-4 lg:mx-0 lg:max-w-none lg:grid-cols-4">
      {data.avg_score_increase && (
        <ComparisonItemNoWrap
          label="Average* Score Increase"
          value={data.avg_score_increase}
        />
      )}
      {data.amount && (
        <ComparisonItemNoWrap label="Loan/Credit Amount" value={data.amount} />
      )}
      {data.monthly_payment && (
        <ComparisonItemNoWrap label="Payment" value={data.monthly_payment} />
      )}
      {data.term && (
        <ComparisonItemNoWrap label="Term Length" value={data.term} />
      )}
    </div>
  )

  const type = data.type || ''
  const callouts = data.callouts || []

  const Cta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const DialogOpenButton = (
    <DialogButton
      as="button"
      onClick={() => toggleDialog(partnerName)}
      className="inline-block whitespace-nowrap p-2 text-sm font-bold text-gray-500 underline hover:text-gray-600 focus:outline-none active:text-gray-800"
    >
      More Info
    </DialogButton>
  )

  const DialogLogo = (
    <img src={data.logo} alt={data.name} className="max-h-[30px]" />
  )

  const DialogDescription = data.description || <></>

  const DialogCta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full max-w-[275px] rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const Disclaimer = data.disclaimer || <></>
  const isOpen = open[partnerName] || false
  const handleClose = () => toggleDialog(partnerName)

  return (
    <CompareOffer
      className={className}
      Logo={Logo}
      Comparison={Comparison}
      Cta={Cta}
      type={type}
      callouts={callouts}
      DialogOpenButton={DialogOpenButton}
      DialogLogo={DialogLogo}
      DialogDescription={DialogDescription}
      DialogCta={DialogCta}
      Disclaimer={Disclaimer}
      isOpen={isOpen}
      handleClose={handleClose}
    />
  )
}

export const CompareOfferInvesting = ({
  className,
  ctaUrl,
  partnerName,
  trackEvent,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleDialog = (partnerName: string) => {
    const willOpen = !open[partnerName]

    // Track event when opening the dialog
    if (willOpen && trackEvent) {
      trackEvent('view_compare_investing_more_info', {
        partner: data.name,
      })
    }

    setOpen((prevState) => ({
      ...prevState,
      [partnerName]: !prevState[partnerName],
    }))
  }

  const Logo = (
    <div style={{ height: data.height, width: data.width }}>
      <Link href={ctaUrl} target="_blank">
        <img className={`h-[32px] w-auto`} src={data.logo} alt={data.name} />
      </Link>
    </div>
  )

  const Comparison = (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-y-3 md:grid-cols-2 lg:grid-cols-3 lg:items-start lg:gap-x-4">
      {data.account_types && (
        <ComparisonItemWrapLabelAbove
          className="sm:order-1 sm:col-span-2 md:order-1 md:col-span-2 lg:order-1 lg:col-span-2"
          label="Account Types"
          value={data.account_types.join(', ')}
        />
      )}
      {data.best_for && (
        <ComparisonItemWrapLabelAbove
          className="order-4 sm:order-2 sm:col-span-2 md:order-2 md:col-span-2 lg:order-3 lg:col-span-2"
          label="Best For"
          value={data.best_for}
        />
      )}
      {data.minimum_deposit && (
        <ComparisonItemWrapLabelAbove
          className="sm:order-3 md:order-3 lg:order-2"
          label="Minimum Deposit"
          value={data.minimum_deposit}
        />
      )}
      {data.monthly_payment && (
        <ComparisonItemWrapLabelAbove
          className="sm:order-4 md:order-4 lg:order-4"
          label="Fees"
          value={data.monthly_payment}
        />
      )}
    </div>
  )

  const type = data.type || ''
  const callouts = data.callouts || []

  const Cta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const DialogOpenButton = (
    <DialogButton
      as="button"
      onClick={() => toggleDialog(partnerName)}
      className="inline-block whitespace-nowrap p-2 text-sm font-bold text-gray-500 underline hover:text-gray-600 focus:outline-none active:text-gray-800"
    >
      More Info
    </DialogButton>
  )

  const DialogLogo = (
    <img src={data.logo} alt={data.name} className="max-h-[30px]" />
  )

  const DialogFeatures = data.features || []
  const DialogDescription = data.description || <></>

  const DialogCta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full max-w-[275px] rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const Disclaimer = data.disclaimer || <></>
  const isOpen = open[partnerName] || false
  const handleClose = () => toggleDialog(partnerName)

  return (
    <CompareOffer
      className={className}
      Logo={Logo}
      Comparison={Comparison}
      Cta={Cta}
      type={type}
      callouts={callouts}
      DialogOpenButton={DialogOpenButton}
      DialogLogo={DialogLogo}
      DialogFeatures={DialogFeatures}
      DialogDescription={DialogDescription}
      DialogCta={DialogCta}
      Disclaimer={Disclaimer}
      isOpen={isOpen}
      handleClose={handleClose}
    />
  )
}

export const CompareOfferBankAccount = ({
  className,
  ctaUrl,
  partnerName,
  trackEvent,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleDialog = (partnerName: string) => {
    const willOpen = !open[partnerName]

    // Track event when opening the dialog
    if (willOpen && trackEvent) {
      trackEvent('view_compare_bank_account_more_info', {
        partner: data.name,
      })
    }

    setOpen((prevState) => ({
      ...prevState,
      [partnerName]: !prevState[partnerName],
    }))
  }

  const Logo = (
    <div style={{ height: data.height, width: data.width }}>
      <Link href={ctaUrl} target="_blank">
        <img className={`h-[32px] w-auto`} src={data.logo} alt={data.name} />
      </Link>
    </div>
  )

  const Comparison = (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2 sm:gap-y-3 md:grid-cols-2 lg:grid-cols-3 lg:items-start lg:gap-x-4">
      {data.minimum_deposit && (
        <ComparisonItemWrapLabelAbove
          label="Opening Deposit"
          value={data.minimum_deposit}
        />
      )}
      {data.minimum_balance && (
        <ComparisonItemWrapLabelAbove
          label="Minimum Balance"
          value={data.minimum_balance}
        />
      )}
      {data.overdraft_fee && (
        <ComparisonItemWrapLabelAbove
          label="Overdraft Fee"
          value={data.overdraft_fee}
        />
      )}
      {data.monthly_payment && (
        <ComparisonItemWrapLabelAbove
          className="lg:order-1"
          label="Monthly Fee"
          value={data.monthly_payment}
        />
      )}
      <ComparisonItemWrapLabelAbove
        className="sm:col-span-2 md:col-span-2"
        label="Best For"
        value={data.best_for}
      />
    </div>
  )

  const type = data.type || ''
  const callouts = data.callouts || []

  const Cta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const DialogOpenButton = (
    <DialogButton
      as="button"
      onClick={() => toggleDialog(partnerName)}
      className="inline-block whitespace-nowrap p-2 text-sm font-bold text-gray-500 underline hover:text-gray-600 focus:outline-none active:text-gray-800"
    >
      More Info
    </DialogButton>
  )

  const DialogLogo = (
    <img src={data.logo} alt={data.name} className="max-h-[30px]" />
  )

  const DialogFeatures = data.features || []
  const DialogDescription = data.description || <></>

  const DialogCta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full max-w-[275px] rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const Disclaimer = data.disclaimer || <></>
  const isOpen = open[partnerName] || false
  const handleClose = () => toggleDialog(partnerName)

  return (
    <CompareOffer
      className={className}
      Logo={Logo}
      Comparison={Comparison}
      Cta={Cta}
      type={type}
      callouts={callouts}
      DialogOpenButton={DialogOpenButton}
      DialogLogo={DialogLogo}
      DialogFeatures={DialogFeatures}
      DialogDescription={DialogDescription}
      DialogCta={DialogCta}
      Disclaimer={Disclaimer}
      isOpen={isOpen}
      handleClose={handleClose}
    />
  )
}

export const CompareOfferBudgeting = ({
  className,
  ctaUrl,
  partnerName,
  trackEvent,
}: OfferProps) => {
  const data = partnerData[partnerName]

  if (!data) {
    return null
  }

  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleDialog = (partnerName: string) => {
    const willOpen = !open[partnerName]

    // Track event when opening the dialog
    if (willOpen && trackEvent) {
      trackEvent('view_compare_budgeting_more_info', {
        partner: data.name,
      })
    }

    setOpen((prevState) => ({
      ...prevState,
      [partnerName]: !prevState[partnerName],
    }))
  }

  const Logo = (
    <div style={{ height: data.height, width: data.width }}>
      <Link href={ctaUrl} target="_blank">
        <img className={`h-[32px] w-auto`} src={data.logo} alt={data.name} />
      </Link>
    </div>
  )

  const Comparison = (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2 sm:gap-y-3 md:grid-cols-2 lg:grid-cols-3 lg:items-start lg:gap-x-4">
      {data.monthly_payment && (
        <ComparisonItemWrapLabelAbove
          className="lg:order-1"
          label="Monthly Fee"
          value={data.monthly_payment}
        />
      )}
      <ComparisonItemWrapLabelAbove
        className="sm:col-span-2 md:col-span-2"
        label="Best For"
        value={data.best_for}
      />
    </div>
  )

  const type = data.type || ''
  const callouts = data.callouts || []

  const Cta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const DialogOpenButton = (
    <DialogButton
      as="button"
      onClick={() => toggleDialog(partnerName)}
      className="inline-block whitespace-nowrap p-2 text-sm font-bold text-gray-500 underline hover:text-gray-600 focus:outline-none active:text-gray-800"
    >
      More Info
    </DialogButton>
  )

  const DialogLogo = (
    <img src={data.logo} alt={data.name} className="max-h-[30px]" />
  )

  const DialogFeatures = data.features || []
  const DialogDescription = data.description || <></>

  const DialogCta = (
    <Button
      href={ctaUrl}
      target="_blank"
      className="font-display w-full max-w-[275px] rounded-full !bg-[#0a3754] py-4 text-base text-white hover:!bg-[#0a3754]/90"
    >
      Continue
    </Button>
  )

  const Disclaimer = data.disclaimer
  const isOpen = open[partnerName] || false
  const handleClose = () => toggleDialog(partnerName)

  return (
    <CompareOffer
      className={className}
      Logo={Logo}
      Comparison={Comparison}
      Cta={Cta}
      type={type}
      callouts={callouts}
      DialogOpenButton={DialogOpenButton}
      DialogLogo={DialogLogo}
      DialogFeatures={DialogFeatures}
      DialogDescription={DialogDescription}
      DialogCta={DialogCta}
      Disclaimer={Disclaimer}
      isOpen={isOpen}
      handleClose={handleClose}
    />
  )
}
